// The synchronization matrix the run wrote for itself: units down the side, phases across the top,
// every executed order card and key event in the cell where it happened (spec §2 Stage 4). A staff
// normally builds this before the fight; here the sim builds the one that actually occurred, so the
// plan and the execution can be read against each other.

import { useMemo } from 'react';
import { buildSyncMatrix } from '../../debrief/analyze';
import type { Event, Graphic, Unit } from '../../sim/types';
import { Heading } from './Logs';

export function SyncMatrix({ events, units, graphics }: { events: Event[]; units: Unit[]; graphics: Graphic[] }) {
  const matrix = useMemo(() => buildSyncMatrix(events, units, graphics), [events, units, graphics]);

  return (
    <section data-testid="sync-matrix" className="wt-panel p-3">
      <Heading>Synchronization matrix</Heading>
      <p className="mt-1 text-[11px] leading-snug text-fog">
        What each platoon was actually doing in each phase of the attack. Phases are cut at the minute
        your lead element crossed each phase line. An empty cell is a platoon that did nothing in that
        phase.
      </p>
      <div className="thin-scroll mt-2 overflow-auto">
        <table className="mono w-full border-collapse text-[11px]">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-[0.14em] text-fog">
              <th className="w-24 px-1.5 py-1 font-normal">Unit</th>
              {matrix.phases.map((p) => (
                <th key={p} className="px-1.5 py-1 font-normal" style={{ color: 'var(--color-friendly)' }}>{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.unitId} data-testid="sync-row" className="border-t border-slate-800 align-top">
                <td className="whitespace-nowrap px-1.5 py-1 text-paper">{row.callsign}</td>
                {row.cells.map((cell, i) => (
                  <td
                    key={i}
                    className="px-1.5 py-1 leading-snug"
                    style={{ color: cell ? 'var(--color-paper)' : 'var(--color-fog)', opacity: cell ? 1 : 0.35 }}
                  >
                    {cell || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
